import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { API } from "../../../config/api";
import { del, get, post, put } from "../../../lib/http";
import { AD } from "../../adminUi.js";
import RichText from "../events/RichText.jsx";

const cn = (...a) => a.filter(Boolean).join(" ");
const ACCEPT_IMG = ["image/jpeg", "image/png", "image/webp"];
const MAX_MB = 5;

const initialForm = {
  id: "",
  title: "",
  imageUrl: "",
  contentHtml: "",
  isActive: true,
  startsAt: "",
  endsAt: "",
};

function toInputDateTime(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
}

export default function AdminImportantNotice() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const res = await get(API.notice.adminList);
      const list = Array.isArray(res) ? res : [];
      setRows(list);
      if (!form.id && list.length) {
        fillForm(list[0]);
      }
    } catch (err) {
      console.error("fetch notice list failed:", err);
      toast.error(err?.message || "Failed to load notices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r?.title, r?.contentHtml, r?.createdBy?.name, r?.updatedBy?.name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [rows, q]);

  function fillForm(item) {
    if (!item) {
      setForm(initialForm);
      return;
    }
    setForm({
      id: item._id || "",
      title: item.title || "",
      imageUrl: item.imageUrl || "",
      contentHtml: item.contentHtml || "",
      isActive: item.isActive !== false,
      startsAt: toInputDateTime(item.startsAt),
      endsAt: toInputDateTime(item.endsAt),
    });
  }

  const onUpload = async (file) => {
    if (!file) return;
    if (!ACCEPT_IMG.includes(file.type)) {
      toast.warn("Please upload JPG / PNG / WEBP image");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.warn(`Image should be under ${MAX_MB}MB`);
      return;
    }

    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);
      const resp = await post(API.events.fileUploader, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = resp?.url || "";
      if (!url) {
        toast.error("Upload failed (no URL)");
        return;
      }
      setForm((s) => ({ ...s, imageUrl: url }));
      toast.success("Image uploaded");
    } catch (err) {
      console.error("notice image upload failed:", err);
      toast.error(err?.message || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    const contentHtml = form.contentHtml.trim();
    if (!title) return toast.warn("Title is required");
    if (!contentHtml) return toast.warn("Notice content is required");

    const payload = {
      title,
      imageUrl: form.imageUrl.trim(),
      contentHtml,
      isActive: !!form.isActive,
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
      endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
    };

    try {
      setSaving(true);
      if (form.id) {
        await put(API.notice.update(form.id), payload);
        toast.success("Notice updated");
      } else {
        const res = await post(API.notice.create, payload);
        fillForm(res);
        toast.success("Notice created");
      }
      await fetchRows();
    } catch (err) {
      console.error("save notice failed:", err);
      toast.error(err?.message || "Failed to save notice");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this notice?")) return;
    try {
      await del(API.notice.delete(id));
      toast.success("Notice deleted");
      if (form.id === id) setForm(initialForm);
      await fetchRows();
    } catch (err) {
      console.error("delete notice failed:", err);
      toast.error(err?.message || "Failed to delete notice");
    }
  };

  return (
    <div className={cn(AD.formWrapWide, AD.page)}>
      <div className={AD.headerRow}>
        <div>
          <h1 className={AD.title}>Important Notice Popup</h1>
          <p className={AD.subtitle}>
            Admin se notice image + rich text save karein. App open par users ko popup dikhega.
          </p>
        </div>
      </div>

      <form onSubmit={onSave} className={cn(AD.filterCard, "space-y-4")}>
        <div className={AD.formGrid2}>
          <div>
            <label className={AD.fieldLabel}>Notice title</label>
            <input
              className={AD.inputField}
              value={form.title}
              onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
              placeholder="Important update for users"
              required
            />
          </div>
          <div className="space-y-2">
            <label className={AD.fieldLabel}>Notice image (optional)</label>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className={AD.btnSecondary}
                disabled={uploading}
              >
                {uploading ? "Uploading…" : "Upload image"}
              </button>
              {form.imageUrl ? (
                <button
                  type="button"
                  className={AD.btnGhost}
                  onClick={() => setForm((s) => ({ ...s, imageUrl: "" }))}
                >
                  Remove
                </button>
              ) : null}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])}
            />
            {form.imageUrl ? (
              <img
                src={form.imageUrl}
                alt="Notice"
                className="mt-2 h-24 rounded-xl border object-cover"
              />
            ) : null}
          </div>
        </div>

        <div className={AD.formGrid2}>
          <div>
            <label className={AD.fieldLabel}>Show from (optional)</label>
            <input
              type="datetime-local"
              className={AD.inputField}
              value={form.startsAt}
              onChange={(e) => setForm((s) => ({ ...s, startsAt: e.target.value }))}
            />
          </div>
          <div>
            <label className={AD.fieldLabel}>Show until (optional)</label>
            <input
              type="datetime-local"
              className={AD.inputField}
              value={form.endsAt}
              onChange={(e) => setForm((s) => ({ ...s, endsAt: e.target.value }))}
            />
          </div>
        </div>

        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((s) => ({ ...s, isActive: e.target.checked }))}
          />
          <span className="text-sm font-medium text-slate-700">Active notice</span>
        </label>

        <RichText
          label="Popup content (CKEditor)"
          value={form.contentHtml}
          onChange={(v) => setForm((s) => ({ ...s, contentHtml: v }))}
          placeholder="Important notice text..."
          minHeight={240}
        />

        <div className="flex flex-wrap gap-2">
          <button type="submit" className={AD.btnPrimary} disabled={saving}>
            {saving ? "Saving…" : form.id ? "Update notice" : "Create notice"}
          </button>
          <button
            type="button"
            className={AD.btnSecondary}
            onClick={() => setForm(initialForm)}
            disabled={saving}
          >
            New notice
          </button>
        </div>
      </form>

      <div className={AD.filterCard}>
        <div className={AD.headerRow}>
          <h2 className="text-lg font-bold text-slate-900">Saved notices</h2>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className={cn(AD.inputField, "max-w-xs")}
            placeholder="Search notices..."
          />
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-500">No notices found.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {filtered.map((n) => (
              <div
                key={n._id}
                className="rounded-xl border border-slate-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900 dark:text-white">
                      {n.title}
                    </p>
                    <p className="text-xs text-slate-500">
                      {n.isActive ? "Active" : "Inactive"} • Updated{" "}
                      {new Date(n.updatedAt).toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={AD.btnSecondary}
                      onClick={() => fillForm(n)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={AD.btnDanger}
                      onClick={() => onDelete(n._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
